import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const InventorySidebar = ({ filters, onFiltersChange, onClearFilters }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const designOptions = [
    { value: 'all', label: 'All Designs' },
    { value: 'floral-print', label: 'Floral Print' },
    { value: 'geometric', label: 'Geometric Pattern' },
    { value: 'abstract', label: 'Abstract Design' },
    { value: 'traditional', label: 'Traditional Motif' },
    { value: 'modern', label: 'Modern Art' }
  ];

  const sizeOptions = [
    { value: 'all', label: 'All Sizes' },
    { value: 'S', label: 'Small (S)' },
    { value: 'M', label: 'Medium (M)' },
    { value: 'L', label: 'Large (L)' },
    { value: 'XL', label: 'Extra Large (XL)' },
    { value: '2XL', label: '2X Large (2XL)' }
  ];

  const stockStatusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'in-stock', label: 'In Stock' },
    { value: 'low-stock', label: 'Low Stock' },
    { value: 'out-of-stock', label: 'Out of Stock' },
    { value: 'reserved', label: 'Reserved' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'warehouse-a', label: 'Warehouse A' },
    { value: 'warehouse-b', label: 'Warehouse B' },
    { value: 'production-floor', label: 'Production Floor' },
    { value: 'quality-check', label: 'Quality Check Area' },
    { value: 'dispatch', label: 'Dispatch Section' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleCheckboxChange = (key, checked) => {
    onFiltersChange({
      ...filters,
      [key]: checked
    });
  };

  return (
  <div
    className={`relative bg-card border-r border-border transition-all duration-300 
    ${isCollapsed ? 'w-full sm:w-16' : 'w-full sm:w-64 lg:w-64'} 
    ${isCollapsed ? 'p-1' : 'p-4'} 
    h-auto lg:h-[calc(100vh-4rem)] overflow-y-auto 
    mt-10 sm:mt-0`}
  >
    {/* Filters Header - Always visible */}
<div className="relative z-20 bg-card border-b border-border px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between">
  <div className="flex items-center gap-2 min-w-0">
    <Icon
      name="Filter"
      size={18}
      className="text-primary flex-shrink-0"
    />
    <h3 className="font-semibold text-foreground truncate text-sm sm:text-base">
      Filters
    </h3>
  </div>
  <div className="flex-shrink-0 ml-2">
    <Button
      variant="ghost"
      size="sm"
      className="h-8 w-8 p-0  hover:bg-transparent"
      onClick={() => setIsCollapsed(!isCollapsed)}
    >
      {/* Mobile icons */}
      <Icon
        name={isCollapsed ? "ChevronDown" : "ChevronUp"}
        size={16}
        className="sm:hidden"
      />
      {/* Desktop icons */}
      <Icon
        name={isCollapsed ? "ChevronRight" : "ChevronLeft"}
        size={16}
        className="hidden sm:inline"
      />
    </Button>
  </div>
</div>

    {/* Filters body - collapsed or expanded */}
    <div
      className={`transition-all duration-300 overflow-y-auto flex-1 
        ${isCollapsed ? 'hidden sm:block sm:opacity-0 sm:h-0' : 'block opacity-100 h-auto'}`}
    >
      <div className="space-y-4 mt-4">
        {/* Quick Search */}
        <Input
          label="Quick Search"
          type="search"
          placeholder="Search by design, barcode..."
          value={filters.search || ''}
          onChange={(e) => handleFilterChange('search', e.target.value)}
        />

        {/* Design Filter */}
        <Select
          label="Design Category"
          options={designOptions}
          value={filters.design || 'all'}
          onChange={(value) => handleFilterChange('design', value)}
        />

        {/* Size Filter */}
        <Select
          label="Size"
          options={sizeOptions}
          value={filters.size || 'all'}
          onChange={(value) => handleFilterChange('size', value)}
        />

        {/* Stock Status Filter */}
        <Select
          label="Stock Status"
          options={stockStatusOptions}
          value={filters.stockStatus || 'all'}
          onChange={(value) => handleFilterChange('stockStatus', value)}
        />

        {/* Location Filter */}
        <Select
          label="Location"
          options={locationOptions}
          value={filters.location || 'all'}
          onChange={(value) => handleFilterChange('location', value)}
        />

        {/* Stock Range */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">Stock Range</label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={filters.minStock || ''}
              onChange={(e) => handleFilterChange('minStock', e.target.value)}
            />
            <Input
              type="number"
              placeholder="Max"
              value={filters.maxStock || ''}
              onChange={(e) => handleFilterChange('maxStock', e.target.value)}
            />
          </div>
        </div>

        {/* Additional Filters */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">Additional Options</label>
          <div className="space-y-2">
            <Checkbox
              label="Show only barcoded items"
              checked={filters.onlyBarcoded || false}
              onChange={(e) => handleCheckboxChange('onlyBarcoded', e.target.checked)}
            />
            <Checkbox
              label="Include reserved stock"
              checked={filters.includeReserved || false}
              onChange={(e) => handleCheckboxChange('includeReserved', e.target.checked)}
            />
            <Checkbox
              label="Show defective pieces"
              checked={filters.showDefective || false}
              onChange={(e) => handleCheckboxChange('showDefective', e.target.checked)}
            />
          </div>
        </div>

        {/* Clear Filters */}
        <div className="pt-4 border-t border-border">
          <Button
            variant="outline"
            fullWidth
            onClick={onClearFilters}
            iconName="RotateCcw"
            iconPosition="left"
            iconSize={16}
          >
            Clear All Filters
          </Button>
        </div>
      </div>
    </div>
  </div>
);

};

export default InventorySidebar;